import useModalManager from '@hooks/useModalManager';
import { Badge } from '@ui';
import { useTranslation } from 'react-i18next';
import SelectComponent, { Option } from '../../SelectComponent';
import ClubMemberPickerModal from './ClubMemberPickerModal';
import { MatchCandidate, PreviewRow, RowDecision } from './types';

interface Props {
    row: PreviewRow;
    decision: RowDecision;
    /** 1-based number of the earlier row that already targets the same member, if any. */
    collisionWithRow?: number;
    onChange: (decision: RowDecision) => void;
}

/** Renders a resolved value as text, ignoring anything that is not a plain scalar. */
const formatValue = (value: any) => (value === null || value === '' || typeof value === 'object' ? '' : String(value));

/** One preview row: booking data on the left, the assignment control on the right. */
const MatchRow = ({ row, decision, collisionWithRow, onChange }: Props) => {
    // Provides translated strings for actions, reasons and the collision hint.
    const { t } = useTranslation('bookings');
    // Opens and closes the member picker modal.
    const { addModal, removeModal } = useModalManager();

    // Merge candidates plus the manually searched target, deduplicated, so it stays selectable even though it is not a suggested candidate.
    const targets: MatchCandidate[] = [
        ...row.candidates,
        ...(decision.manualTarget && !row.candidates.some((c) => c.id === decision.manualTarget?.id)
            ? [decision.manualTarget]
            : [])
    ];

    // Builds the dropdown options for create/skip plus one per merge target.
    const options: Option[] = [
        { label: t('booking.createMember.actions.create'), value: 'create' },
        { label: t('booking.createMember.actions.skip'), value: 'skip' },
        ...targets.map((candidate) => ({
            label: t('booking.createMember.actions.merge', {
                name: candidate.name,
                number: candidate.number ?? '',
                score: candidate.score
            }),
            value: candidate.id
        }))
    ];

    // Resolves the option value that represents the current decision.
    const selectedValue = decision.action === 'merge' ? (decision.member_id ?? undefined) : decision.action;

    // The candidate the decision currently points at, if any: only its reasons are shown as badges, so a
    // badge can never describe a candidate other than the one actually selected. Manually searched targets
    // carry no reasons, and create/skip decisions have no target at all, so both correctly render no badges.
    const selectedCandidate =
        decision.action === 'merge' ? targets.find((candidate) => candidate.id === decision.member_id) : undefined;

    /** Applies the selection of the assignment dropdown. */
    const handleSelect = (value: Option['value']) => {
        if (value === 'create' || value === 'skip') {
            onChange({ ...decision, action: value, member_id: null });
            return;
        }

        onChange({ ...decision, action: 'merge', member_id: Number(value) });
    };

    /** Opens the member picker and turns the chosen member into this row's merge target. */
    const handleOpenPicker = () =>
        addModal({
            title: t('booking.createMember.picker.title'),
            content: (id) => (
                <ClubMemberPickerModal
                    onSelect={(candidate) => {
                        onChange({ action: 'merge', member_id: candidate.id, manualTarget: candidate });
                        removeModal(id);
                    }}
                />
            ),
            size: 70,
            show: true
        });

    return (
        <tr className={collisionWithRow ? 'tw-bg-errorLight' : undefined}>
            <td className="tw-p-2 tw-align-top">
                <div className="tw-font-semibold">{row.booking.user_name}</div>
                <div className="tw-text-sm tw-text-accents-6">{row.booking.user_email}</div>
            </td>
            <td className="tw-p-2 tw-align-top">
                <div>{formatValue(row.resolved.name)}</div>
                <div className="tw-text-sm tw-text-accents-6">{formatValue(row.resolved.email)}</div>
            </td>
            <td className="tw-p-2 tw-align-top">
                <SelectComponent options={options} value={selectedValue} onChange={handleSelect} />
                <div className="tw-mt-1 tw-flex tw-flex-wrap tw-gap-1">
                    {selectedCandidate?.reasons.map((reason) => (
                        <Badge key={reason} flat color="light" size="xs">
                            {t(`booking.createMember.reasons.${reason}`)}
                        </Badge>
                    ))}
                </div>
                {collisionWithRow && (
                    <p className="tw-m-0 tw-mt-1 tw-text-sm tw-text-error">
                        {t('booking.createMember.collision', { row: collisionWithRow })}
                    </p>
                )}
                <button
                    type="button"
                    className="tw-mt-1 tw-border-none tw-bg-transparent tw-p-0 tw-text-sm tw-text-primary tw-underline"
                    onClick={handleOpenPicker}
                >
                    {t('booking.createMember.actions.search')}
                </button>
            </td>
        </tr>
    );
};

export default MatchRow;
